import i18n from '../i18n';

/**
 * Get the appropriate language version of an AI analysis field.
 * Checks for language-specific versions (e.g., aiAnalysis_en, aiAnalysis_es)
 * and falls back to the base field.
 * 
 * @param {Object} data - The document (incident report, audit, etc.)
 * @param {string} field - The base field name (e.g., 'aiAnalysis', 'aiHazardSummary')
 * @returns The field value in the user's preferred language
 */
export function getLocalizedAI(data, field) {
  if (!data) return null;
  
  const lang = i18n.language || 'en';
  
  // Try language-specific field first (e.g., aiAnalysis_en, aiAnalysis_es)
  const langField = `${field}_${lang}`;
  if (data[langField] !== undefined && data[langField] !== null) {
    return data[langField];
  }
  
  // Fall back to base field
  return data[field] || null;
}

/**
 * Potential hazards with their prose in the reader's language.
 *
 * Severity, OSHA reference, the imminent-danger flag and the review decision always come from
 * the canonical record — only the wording is swapped, so a translation can never change what
 * was found or how serious it is.
 */
export function getLocalizedHazards(audit) {
  const hazards = audit?.aiPotentialHazards || [];
  if (!hazards.length) return hazards;

  const lang = i18n.language || 'en';
  const prose = audit?.[`aiPotentialHazardsText_${lang}`];
  if (!prose) return hazards;

  return hazards.map((hazard) => {
    const translated = prose[hazard.id];
    if (!translated) return hazard;
    const merged = { ...hazard };
    Object.entries(translated).forEach(([key, value]) => {
      if (value && (!Array.isArray(value) || value.length)) merged[key] = value;
    });
    return merged;
  });
}
